/**
 * The bag, ground loot, the hidden weapon and the smiths (docs/handoff/newdir/ITEMS.md):
 *
 * - **Bag order.** `bagMove` moves an item from one bag slot to another (the bag is `progress.inventory`, in order;
 *   worn items stay in it, marked as worn, as before).
 * - **Ground loot.** A monster's drops (its `loot` list, then its table in data/drops.ts) fall round where it died
 *   (`dropLoot`, called by the World's kill), and so does whatever the player throws out of the bag (`dropItem`,
 *   `dropGood`). A drop lies with the glint of its rarity until the player walks over it (`playerStep`) or presses
 *   the pick-up key (`pickup`), and is gone after `LOOT.ttl` seconds. Not saved: a zone's drops go with the zone.
 * - **The hidden weapon.** `throwHidden` looses the move of the item in the อาวุธลับ slot (data/hiddenWeapons.ts).
 * - **The smiths.** `setup` puts this zone's smiths (data/smiths.ts) by the plaza; talking to one (or to any
 *   townsperson titled ช่างตีเหล็ก) opens the forge (`smithForge`), and `forge`/`infuse` are refused away from them.
 */
import { BREAKTHROUGH_DROPS } from '../data/breakthrough';
import { SIGNATURE, SIGNATURE_AFFIX, SIGNATURE_OF_FAMILY, codexFamilyOf } from '../data/codex';
import { DROP_TABLES, dropRarity, LOOT, type DropEntry } from '../data/drops';
import { BAG_SIZE, ITEMS, composeRoll, itemOf, rollItem, type ItemRoll, type Rarity } from '../data/items';
import { SCHOOLS } from '../data/schools';
import { MARTIAL_ARTS } from '../data/skills';
import type { MonsterDef } from '../data/monsters';
import { GOODS, GOOD_STACK } from '../data/professions';
import { SMITH_REACH, SMITH_TITLE, SMITHS } from '../data/smiths';
import { autoMul } from './autoRules';
import { freeAt } from '../path';
import { randInt } from '../rng';
import type { Entity, PlayerCommand, World } from '../world';
import type { ItemCraftCommand, ItemCraftEvent } from './itemCraft';
import type { SystemHost, WorldSystem } from './types';

/** A drop lying on the ground: an equipment item (fixed, or a rolled one the player threw out) or a stack of goods. */
export interface GroundLoot {
  readonly id: number;
  readonly x: number;
  readonly y: number;
  /** An item id (data/items) with no roll, or the base of `roll`. */
  readonly item?: string;
  readonly roll?: ItemRoll;
  /** A good id (data/professions) and how many. */
  readonly good?: string;
  readonly n: number;
  readonly rarity: Rarity;
  /** The name shown over it. */
  readonly name: string;
  /** Seconds it has lain, and how long it lies. */
  t: number;
  /** Thrown out by the player where they stand: not picked up by walking until they have stepped off it. */
  held?: boolean;
  readonly ttl: number;
}

/** Commands (part of `PlayerCommand`). */
export type ItemsCommand =
  /** Move the item in bag slot `from` to slot `to` (indexes into `progress.inventory`). */
  | { t: 'bagMove'; from: number; to: number }
  /** Throw an item (one copy) or goods out of the bag onto the ground at the player's feet. */
  | { t: 'dropItem'; id: string }
  | { t: 'dropGood'; id: string; n?: number }
  /** Pick up a drop (`loot`, or the nearest within the key's reach). */
  | { t: 'pickup'; loot?: number }
  /** Throw the hidden weapon in the อาวุธลับ slot. */
  | { t: 'throwHidden' }
  /** Items v2 smith actions (systems/itemCraft.ts). */
  | ItemCraftCommand;

/** Events (part of `WorldEvent`). */
export type ItemsEvent =
  /** A drop fell to the ground (from a kill, or out of the bag). */
  | { type: 'lootDrop'; loot: number; x: number; y: number; rarity: Rarity }
  /** A drop was picked up (`picked`), or faded away. */
  | { type: 'lootGone'; loot: number; picked: boolean }
  /** A smith was spoken to: open the forge window. */
  | { type: 'smithForge'; npcId: number }
  | ItemCraftEvent;

const SYSTEMS = new WeakMap<World, ItemsSystem>();

/** The drops lying in this world's zone (empty for a world without the system, such as a network replica). */
export const groundLootOf = (w: World): readonly GroundLoot[] => SYSTEMS.get(w)?.loot ?? [];

/** Whether the player stands within reach of a smith in this world. */
export const nearSmith = (w: World): boolean => SYSTEMS.get(w)?.nearSmith() ?? false;

/** The nearest free ground to (x, y) within `r` metres, or the point itself. */
function freeNear(w: World, x: number, y: number, r = 6): { x: number; y: number } {
  for (let k = 0; k <= r; k++)
    for (let j = -k; j <= k; j++)
      for (let i = -k; i <= k; i++) {
        if (Math.max(Math.abs(i), Math.abs(j)) !== k) continue;
        const px = Math.floor(x) + i + 0.5;
        const py = Math.floor(y) + j + 0.5;
        if (freeAt(w.map, px, py)) return { x: px, y: py };
      }
  return { x, y };
}

export class ItemsSystem implements WorldSystem {
  readonly name = 'items';
  readonly loot: GroundLoot[] = [];
  private nextLoot = 1;
  private readonly smiths = new Set<number>();

  constructor(private readonly h: SystemHost) {
    SYSTEMS.set(h.world, this);
  }

  setup(): void {
    const m = this.h.world.map;
    for (const s of SMITHS) {
      if (s.zone !== m.zone) continue;
      const base = s.at === 'plaza' ? m.plaza : m.landmarks?.[s.at];
      if (!base) continue;
      const at = freeNear(this.h.world, base.x + s.dx, base.y + s.dy);
      const e = this.h.add({
        kind: 'npc',
        x: at.x,
        y: at.y,
        sprite: s.sprite,
        name: s.name,
        title: SMITH_TITLE,
        facing: s.facing ?? 1,
      });
      this.smiths.add(e.id);
    }
  }

  private isSmith(e: Entity): boolean {
    return e.kind === 'npc' && (this.smiths.has(e.id) || e.title === SMITH_TITLE);
  }

  nearSmith(): boolean {
    const p = this.h.world.player;
    for (const e of this.h.world.entities.values())
      if (this.isSmith(e) && Math.hypot(e.x - p.x, e.y - p.y) <= SMITH_REACH) return true;
    return false;
  }

  talk(npc: Entity): boolean {
    if (!this.isSmith(npc)) return false;
    this.h.face(npc, this.h.world.player);
    this.h.emit({ type: 'smithForge', npcId: npc.id });
    return true;
  }

  refuse(cmd: PlayerCommand): boolean {
    if ((cmd.t === 'forge' || cmd.t === 'infuse') && !this.nearSmith()) {
      this.h.notice('ตีบวกและตีธาตุได้ที่ช่างตีเหล็กเท่านั้น');
      return true;
    }
    return false;
  }

  readonly commands = {
    bagMove: (c: Extract<ItemsCommand, { t: 'bagMove' }>) => this.bagMove(c.from, c.to),
    dropItem: (c: Extract<ItemsCommand, { t: 'dropItem' }>) => this.dropItem(c.id),
    dropGood: (c: Extract<ItemsCommand, { t: 'dropGood' }>) => this.dropGood(c.id, c.n),
    pickup: (c: Extract<ItemsCommand, { t: 'pickup' }>) => this.pickupCmd(c.loot),
    throwHidden: () => this.throwHidden(),
  };

  playerStep(): void {
    const p = this.h.world.player;
    if (p.dead || p.z > 1 || !this.loot.length) return;
    for (const l of [...this.loot]) {
      const near = Math.hypot(l.x - p.x, l.y - p.y) <= LOOT.walkReach;
      if (l.held) l.held = near;
      else if (near) this.pick(l, true);
    }
  }

  step(dt: number): void {
    for (let i = this.loot.length - 1; i >= 0; i--) {
      const l = this.loot[i]!;
      l.t += dt;
      if (l.t < l.ttl) continue;
      this.loot.splice(i, 1);
      this.h.emit({ type: 'lootGone', loot: l.id, picked: false });
    }
  }

  // ── drops ──

  /**
   * A kill's drops: the monster's own `loot` list (data/monsters.ts), then its table (data/drops.ts), each entry
   * rolled on its own, in that order. What drops lies round where it fell.
   */
  dropLoot(m: Entity, def: MonsterDef): void {
    const entries: DropEntry[] = [...(def.loot ?? []), ...(DROP_TABLES[def.id] ?? []), ...(BREAKTHROUGH_DROPS[def.id] ?? []), ...this.signatureEntry(m, def)];
    let k = 0;
    for (const d of entries) {
      if (this.h.random() >= d.chance * autoMul(this.h.world)) continue;
      // Scattered on a fixed spiral round the body (no draws spent on where).
      const a = k * 2.4;
      const r = k === 0 ? 0.2 : LOOT.scatter * (0.5 + 0.5 * (k % 2));
      const at = { x: m.x + Math.cos(a) * r, y: m.y + Math.sin(a) * r };
      const spot = freeAt(this.h.world.map, at.x, at.y) ? at : { x: m.x, y: m.y };
      if ('item' in d) {
        const it = ITEMS[d.item];
        if (!it) continue;
        // Items v2: every equipment drop rolls its own affixes at the monster's level (docs/systems/ITEMS_V2.md §5.1).
        const sig = SIGNATURE_AFFIX[it.id];
        const roll = sig
          ? composeRoll(it.id, def.level, it.rarity, [sig])
          : rollItem(it.id, def.level, (this.h.world.auto.on ? it.rarity : dropRarity(it.rarity, def.level, () => this.h.random())), () => this.h.random(), {
          arts: Object.values(MARTIAL_ARTS).filter((a) => !a.hidden).map((a) => a.id),
          schools: Object.keys(SCHOOLS),
        });
        this.place({ ...spot, item: it.id, roll, n: 1, rarity: roll.rarity ?? it.rarity, name: it.name });
      } else {
        const g = GOODS[d.good];
        if (!g) continue;
        const n = randInt(() => this.h.random(), d.n[0], d.n[1]);
        this.place({ ...spot, good: g.id, n, rarity: 'common', name: g.name });
      }
      k++;
    }
    if (k) this.h.log('fight', `${def.name}ทำของตกไว้ ${k} ชิ้น · เดินผ่านเพื่อเก็บ`);
  }

  /** L7: the family's signature item, about 1 % a kill (x3 from an elite); one entry rolled with the rest. */
  private signatureEntry(m: Entity, def: MonsterDef): DropEntry[] {
    const fam = codexFamilyOf(def.id);
    const item = fam ? SIGNATURE_OF_FAMILY[fam] : undefined;
    if (!item) return [];
    const elite = this.h.world.sys.elites?.isElite(m);
    return [{ item, chance: SIGNATURE.chance * (elite ? SIGNATURE.eliteMul : 1) }];
  }

  private place(o: Omit<GroundLoot, 'id' | 't' | 'ttl' | 'held'>, held = false): GroundLoot {
    const l: GroundLoot = { ...o, id: this.nextLoot++, t: 0, ttl: LOOT.ttl, ...(held ? { held } : {}) };
    this.loot.push(l);
    // The oldest go when too many lie about.
    while (this.loot.length > LOOT.max) {
      const old = this.loot.shift()!;
      this.h.emit({ type: 'lootGone', loot: old.id, picked: false });
    }
    this.h.emit({ type: 'lootDrop', loot: l.id, x: l.x, y: l.y, rarity: l.rarity });
    return l;
  }

  // ── picking up ──

  private pickupCmd(id?: number): void {
    const p = this.h.world.player;
    if (p.dead) return;
    const near = (l: GroundLoot) => Math.hypot(l.x - p.x, l.y - p.y);
    let l: GroundLoot | undefined;
    if (id !== undefined) l = this.loot.find((x) => x.id === id && near(x) <= LOOT.keyReach);
    else for (const x of this.loot) if (near(x) <= LOOT.keyReach && (!l || near(x) < near(l))) l = x;
    if (!l) return this.h.notice('ไม่มีของให้เก็บใกล้ ๆ');
    this.pick(l, false);
  }

  /** Into the bag (an item) or the goods (a stack); a full bag leaves it lying (one notice when walked over). */
  private pick(l: GroundLoot, walked: boolean): void {
    const w = this.h.world;
    const pr = w.progress;
    if (l.good) {
      const got = w.trades.give(l.good, l.n);
      if (got <= 0) {
        if (!walked) this.h.notice(`ถือ${l.name}ได้ไม่เกิน ${GOOD_STACK}`);
        return;
      }
      this.take(l);
      this.h.log('sys', `เก็บ「${l.name}」×${got}`);
      if (got < l.n)
        this.place({ x: l.x, y: l.y, good: l.good, n: l.n - got, rarity: l.rarity, name: l.name });
    } else {
      if (pr.inventory.length >= BAG_SIZE) {
        // Walking over it says so once, then lets it lie.
        if (!walked || l.t > 0) this.h.notice('กระเป๋าเต็ม ทิ้งของบางชิ้นก่อน');
        return;
      }
      let id = l.item!;
      if (l.roll) {
        id = `roll-${pr.nextRoll++}`;
        pr.rolls[id] = l.roll;
      }
      this.take(l);
      pr.inventory.push(id);
      this.h.emit({ type: 'loot', id: w.player.id, item: id });
      this.h.log('fight', `เก็บ「${l.name}」ใส่กระเป๋า`);
    }
    this.h.emit({ type: 'progressChanged' });
  }

  private take(l: GroundLoot): void {
    const i = this.loot.indexOf(l);
    if (i >= 0) this.loot.splice(i, 1);
    this.h.emit({ type: 'lootGone', loot: l.id, picked: true });
  }

  // ── the bag ──

  private bagMove(from: number, to: number): void {
    const inv = this.h.world.progress.inventory;
    if (!Number.isInteger(from) || !Number.isInteger(to) || from < 0 || from >= inv.length) return;
    const dest = Math.max(0, Math.min(inv.length - 1, to));
    if (dest === from) return;
    const [id] = inv.splice(from, 1);
    inv.splice(dest, 0, id!);
    this.h.emit({ type: 'progressChanged' });
  }

  private dropItem(id: string): void {
    const w = this.h.world;
    const pr = w.progress;
    const i = pr.inventory.lastIndexOf(id);
    const it = itemOf(pr.rolls, id);
    if (i < 0 || !it) return;
    const copies = pr.inventory.filter((x) => x === id).length;
    // The last copy of a worn piece comes off first.
    if (copies === 1 && pr.equipment[it.slot] === id) w.apply({ t: 'unequip', slot: it.slot });
    pr.inventory.splice(i, 1);
    const roll = pr.rolls[id];
    if (roll && !pr.inventory.includes(id)) delete pr.rolls[id];
    const p = w.player;
    this.place(
      {
        x: p.x,
        y: p.y,
        ...(roll ? { item: roll.base, roll } : { item: id }),
        n: 1,
        rarity: it.rarity,
        name: it.forge?.plus ? `${it.name} +${it.forge.plus}` : it.name,
      },
      true,
    );
    this.h.log('sys', `ทิ้ง「${it.name}」ไว้บนพื้น`);
    this.h.emit({ type: 'progressChanged' });
  }

  private dropGood(id: string, n = 1): void {
    const w = this.h.world;
    const goods = w.progress.trades.goods;
    const g = GOODS[id];
    const have = goods[id] ?? 0;
    const k = Math.min(have, Math.max(1, Math.floor(n) || 1));
    if (!g || k <= 0) return;
    if (have === k) delete goods[id];
    else goods[id] = have - k;
    const p = w.player;
    this.place({ x: p.x, y: p.y, good: id, n: k, rarity: 'common', name: g.name }, true);
    this.h.log('sys', `ทิ้ง「${g.name}」×${k} ไว้บนพื้น`);
    this.h.emit({ type: 'progressChanged' });
  }

  // ── the hidden weapon ──

  private throwHidden(): void {
    const w = this.h.world;
    const pr = w.progress;
    const id = pr.equipment.hidden;
    const it = id ? itemOf(pr.rolls, id) : undefined;
    if (!it?.throwSkill) return this.h.notice('ยังไม่ได้ใส่อาวุธลับ');
    w.useSkill(w.player, it.throwSkill);
  }
}
